import _ from 'lodash';
import React, { useEffect } from 'react';

// components
import {
  Backdrop,
  Box,
  Card as MuiCard,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';

import CowbellIcon from '../../../_assets/svg/Cowbell.svg';
import { QuoteStepsP250, QuoteStepsP100 } from './QuoteSteps';
import store from '../../../config/storeConfig';
import { ProductTypes } from '../../../types';
import useNotifications from '../../../_lib/notifications/useNotifications';

const MAX_TIMEOUT = 30;
export const GeneratingPolicyLoader = ({
  location,
  history,
  policy,
  quote,
  setDone,
  product,
  isEndorsement,
}: any) => {
  const notifications = useNotifications();
  const classes = useStyles();
  const {
    palette: { common },
  } = useTheme();
  const status = _.get(policy, 'status');
  const policyId = _.get(policy, 'id');
  const agencyBilled = _.get(quote, 'agencyBilled', false);
  // @ts-expect-error TS(2339): Property 'account' does not exist on type '{}'.
  const { paymentProcessor } = store.getState().account;
  const totalPremium = _.get(quote, 'totalPremium');

  useEffect(() => {
    // begin guard
    const timeoutGuard = setTimeout(() => {
      if (status === 'REQUESTED') {
        notifications.warning(
          "Looks like you did everything right but it's taking longer than expected to generate your policy. We'll drop you off somewhere safe and notify you when we've got it!"
        );

        setTimeout(() => {
          setDone(false);
          history.push(`/customer/policies`);
        }, 5000);
      }

      if (status === 'INVALID') {
        notifications.error(
          "Looks like something went wrong while generating your policy. We'll drop you off somewhere safe to try again."
        );

        setTimeout(() => {
          setDone(false);
          history.push(`/customer/policies`);
        }, 5000);
      }

      if (!status) {
        notifications.error(
          "Hmm something doesn't look right... We're sending you back a step just in case you missed something"
        );

        if (isEndorsement) {
          setTimeout(() => {
            setDone(false);
            history.push(`/customer/policies`);
          }, 5000);
        } else {
          setTimeout(() => {
            setDone(false);
            history.push(`/customer/quotes/${quote.id}/attest/${product}`);
          }, 5000);
        }
      }
    }, MAX_TIMEOUT * 1000);
    // end guard

    if (['ACTIVE', 'ISSUED', 'IN FORCE'].includes(status)) {
      notifications.success(
        isEndorsement ? 'MidTerm Endorsement Generated!' : 'Policy Generated!'
      );

      if (agencyBilled || totalPremium <= 0) {
        setTimeout(() => {
          setDone(false);
          history.push(`/customer/policies/`);
        }, 2000);
      } else {
        setTimeout(() => {
          setDone(false);
          history.push(
            `/customer/policies/${policyId}/pay/${paymentProcessor}/${product}`
          );
        }, 2000);
      }
    }

    return () => {
      clearTimeout(timeoutGuard);
    };
    // eslint-disable-next-line
  }, [
    status,
    history,
    quote.id,
    policyId,
    setDone,
    paymentProcessor,
    agencyBilled,
    isEndorsement,
    product,
    totalPremium,
  ]);

  return (
    <>
      {product === ProductTypes.p100 && (
        <QuoteStepsP100
          location={location}
          agencyBilled={quote.agencyBilled}
          isEndorsement={isEndorsement}
        />
      )}
      {product === ProductTypes.p250 && (
        <QuoteStepsP250 location={location} agencyBilled={quote.agencyBilled} />
      )}
      <MuiCard style={{ height: '100%' }}>
        <Box p={3} height="100%">
          <Skeleton />
        </Box>
      </MuiCard>
      <Backdrop className={classes.backdrop} open>
        <h2 className={classes.heading}>
          Your policy is being generated{' '}
          <span>
            {!agencyBilled &&
              quote.totalPremium > 0 &&
              'and you will be taken to payment soon'}
          </span>
        </h2>
        <CircularProgress
          size="25rem"
          // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'number'.
          thickness="2.5"
          className={classes.progress}
        />
        <div className={classes.logo}>
          <CowbellIcon
            id="policy-loader-icon"
            fill1={common.cowbellBlue}
            fill2={common.cowbellGreen2}
            width="8.3333333333rem"
          />
          <div>Generating Policy...</div>
        </div>
      </Backdrop>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.primary.contrastText,
  },

  progress: {
    color: theme.palette.common.cowbellBlue,
  },

  heading: {
    position: 'absolute',
    top: '20%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    color: theme.palette.primary.contrastText,
    fontSize: theme.config.textSizes.canon,
  },

  logo: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',

    color: theme.palette.common.cowbellBlue,

    fontSize: theme.config.textSizes.paragon,
  },
}));
